/**
 * WorkDesk Configuration — Role-specific metric and column definitions
 *
 * Per Surface Atlas §1-3, each role sees different metrics and columns.
 */

import type { WorkspaceRole } from '@/config/toolsManifest';
import type { MetricConfig, WorkDeskCase } from './WorkDesk.types';

/**
 * Role-specific metric configurations
 * Each role gets exactly 4 metrics, with one designated as "needs-action"
 */
const ROLE_METRICS_BASE: Record<Exclude<WorkspaceRole, 'attorney_internal'>, MetricConfig[]> = {
  // Attorney: Active Matters · Demands Outstanding · Settlement Pipeline · Avg Days-to-Demand
  attorney: [
    {
      key: 'active_matters',
      label: 'Active Matters',
      value: '—',
      subtitle: 'Currently open',
    },
    {
      key: 'demands_outstanding',
      label: 'Demands Outstanding',
      value: '—',
      subtitle: 'Awaiting carrier response',
      badge: { text: 'Action Needed', variant: 'warning' },
    },
    {
      key: 'settlement_pipeline',
      label: 'Settlement Pipeline',
      value: '—',
      isMoney: true,
      subtitle: 'Expected recoveries',
    },
    {
      key: 'avg_days_to_demand',
      label: 'Avg Days-to-Demand',
      value: '—',
      subtitle: 'Last 90 days',
    },
  ],

  // Accountant Internal: Pending Review · Attested · Under Analysis · Avg Recovery
  accountant_internal: [
    {
      key: 'pending_review',
      label: 'Pending Review',
      value: '—',
      subtitle: 'Awaiting attestation',
      badge: { text: 'Needs Action', variant: 'warning' },
    },
    {
      key: 'attested',
      label: 'Attested',
      value: '—',
      subtitle: 'This month',
      badge: { text: 'On Track', variant: 'success' },
    },
    {
      key: 'under_analysis',
      label: 'Under Analysis',
      value: '—',
      subtitle: 'In progress',
    },
    {
      key: 'avg_recovery',
      label: 'Avg Recovery',
      value: '—',
      isMoney: true,
      subtitle: 'Per claim',
    },
  ],

  // Accountant External: Available for Review · In Review · Completed · Pending Client
  accountant_external: [
    {
      key: 'available_for_review',
      label: 'Available for Review',
      value: '—',
      subtitle: 'Ready to start',
      badge: { text: 'New', variant: 'success' },
    },
    {
      key: 'in_review',
      label: 'In Review',
      value: '—',
      subtitle: 'Active engagements',
    },
    {
      key: 'completed',
      label: 'Completed',
      value: '—',
      subtitle: 'This month',
    },
    {
      key: 'pending_client',
      label: 'Pending Client',
      value: '—',
      subtitle: 'Awaiting documents',
      badge: { text: 'Blocked', variant: 'warning' },
    },
  ],

  // Data Specialist — SoT rail (FL-5 5.4, dashboard-source-of-truth.md:223-227):
  // Transactions to Classify (the ONE orange) · Claims in Data Stage · Docs Awaiting OCR ·
  // Auto-Classify Rate %. statsKey maps each card to a real field on QueueStatsResponse
  // (backend/app/api/v1/queue_api.py:63-91), fetched live from
  // /api/v1/queue/stats?user_role=data_specialist. auto_classify_rate is null when no
  // classified transactions exist → renders "—" (never a fabricated default).
  data_specialist: [
    {
      key: 'transactions_to_classify',
      label: 'Transactions to Classify',
      value: '—',
      subtitle: 'Uncategorized transactions',
      badge: { text: 'Action Needed', variant: 'warning' },
      statsKey: 'transactions_to_classify',
    },
    {
      key: 'claims_in_data_stage',
      label: 'Claims in Data Stage',
      value: '—',
      subtitle: 'Cases in your data queue',
      statsKey: 'data_specialist',
    },
    {
      key: 'docs_awaiting_ocr',
      label: 'Docs Awaiting OCR',
      value: '—',
      subtitle: 'Uploads not yet parsed',
      statsKey: 'docs_awaiting_ocr',
    },
    {
      key: 'auto_classify_rate',
      label: 'Auto-Classify Rate',
      value: '—',
      subtitle: 'Auto-approved on the classifier gate',
      statsKey: 'auto_classify_rate',
      isPercent: true,
    },
  ],

  // Admin: Active Cases · Users Online · System Health · Pending Tasks
  admin: [
    {
      key: 'active_cases',
      label: 'Active Cases',
      value: '—',
      subtitle: 'Platform-wide',
    },
    {
      key: 'pending_tasks',
      label: 'Pending Tasks',
      value: '—',
      subtitle: 'Require attention',
      badge: { text: 'Action Needed', variant: 'warning' },
    },
    {
      key: 'users_online',
      label: 'Users Online',
      value: '—',
      subtitle: 'Last 15 minutes',
    },
    {
      key: 'system_health',
      label: 'System Health',
      value: '—',
      subtitle: 'All systems operational',
      badge: { text: 'Healthy', variant: 'success' },
    },
  ],

  // Analyst: Active Analysis · Reports Generated · Engine Runs · Avg Confidence
  analyst: [
    {
      key: 'active_analysis',
      label: 'Active Analysis',
      value: '—',
      subtitle: 'In progress',
    },
    {
      key: 'pending_review',
      label: 'Pending Review',
      value: '—',
      subtitle: 'QA queue',
      badge: { text: 'Action Needed', variant: 'warning' },
    },
    {
      key: 'reports_generated',
      label: 'Reports Generated',
      value: '—',
      subtitle: 'This week',
    },
    {
      key: 'avg_confidence',
      label: 'Avg Confidence',
      value: '—',
      subtitle: 'Model predictions',
    },
  ],

  // Client: Case Status · Documents Submitted · Pending Actions · Estimated Timeline
  client: [
    {
      key: 'case_status',
      label: 'Case Status',
      value: '—',
      subtitle: 'Current phase',
    },
    {
      key: 'pending_actions',
      label: 'Pending Actions',
      value: '—',
      subtitle: 'Your turn',
      badge: { text: 'Action Needed', variant: 'warning' },
    },
    {
      key: 'documents_submitted',
      label: 'Documents Submitted',
      value: '—',
      subtitle: 'To date',
    },
    {
      key: 'estimated_timeline',
      label: 'Est. Timeline',
      value: '—',
      subtitle: 'To completion',
    },
  ],
  mediator: [],  // Phase 7 neutral: no WorkDesk metrics (stripped workspace)
  public_adjuster: [],  // PR1: no WorkDesk metrics yet
  carrier_adjuster: [],  // PR1: no WorkDesk metrics yet
};

export const ROLE_METRICS: Record<WorkspaceRole, MetricConfig[]> = {
  ...ROLE_METRICS_BASE,
  attorney_internal: ROLE_METRICS_BASE.attorney,  // mirrors attorney
};

/**
 * Role-specific DataTable column configurations
 */
const ROLE_COLUMNS_BASE: Record<
  Exclude<WorkspaceRole, 'attorney_internal'>,
  Array<{
    key: keyof WorkDeskCase | string;
    label: string;
    sortable?: boolean;
    width?: string;
  }>
> = {
  attorney: [
    { key: 'clientName', label: 'Client / Business', sortable: true, width: '30%' },
    { key: 'lawFirm', label: 'Law Firm', sortable: true, width: '20%' },
    { key: 'status', label: 'Status', sortable: true, width: '15%' },
    { key: 'value', label: 'Claim Value', sortable: true, width: '15%' },
    { key: 'daysStale', label: 'Days Open', sortable: true, width: '10%' },
    { key: 'assignedTo', label: 'Assigned', sortable: true, width: '10%' },
  ],

  accountant_internal: [
    { key: 'clientName', label: 'Client', sortable: true, width: '25%' },
    { key: 'status', label: 'Status', sortable: true, width: '15%' },
    { key: 'value', label: 'Amount', sortable: true, width: '15%' },
    { key: 'daysStale', label: 'Days in Queue', sortable: true, width: '15%' },
    { key: 'assignedTo', label: 'Assigned', sortable: true, width: '15%' },
    { key: 'lastUpdated', label: 'Last Updated', sortable: true, width: '15%' },
  ],

  accountant_external: [
    { key: 'clientName', label: 'Client', sortable: true, width: '30%' },
    { key: 'status', label: 'Status', sortable: true, width: '20%' },
    { key: 'value', label: 'Amount', sortable: true, width: '20%' },
    { key: 'daysStale', label: 'Days Open', sortable: true, width: '15%' },
    { key: 'lastUpdated', label: 'Updated', sortable: true, width: '15%' },
  ],

  data_specialist: [
    { key: 'clientName', label: 'Case', sortable: true, width: '22%' },
    { key: 'status', label: 'Queue', sortable: true, width: '24%' },
    // P0.2/P0.4: the dead `needsAttention` boolean column is replaced by the
    // backend's actual next-action sentence. `assignedTo` and `daysStale` are
    // dropped from this worklist — redundant on the specialist's own desk
    // (every row is theirs; recency reads from the Updated column) and they
    // crowded the row, clipping the Next Action + action button.
    { key: 'nextAction', label: 'Next Action', sortable: false, width: '22%' },
    { key: 'lastUpdated', label: 'Updated', sortable: true, width: '10%' },
    // Real <button> deep-linking to `primaryActionUrl` (P0.4).
    { key: 'primaryAction', label: '', sortable: false, width: '14%' },
    // FL-5 5.3: compact icon deep-link to the DS per-case extraction review
    // (tier trail + certify). Icon-only so it never clips the row.
    { key: 'extractionReview', label: '', sortable: false, width: '8%' },
  ],

  admin: [
    { key: 'clientName', label: 'Case', sortable: true, width: '25%' },
    { key: 'lawFirm', label: 'Firm', sortable: true, width: '20%' },
    { key: 'status', label: 'Status', sortable: true, width: '15%' },
    { key: 'assignedTo', label: 'Owner', sortable: true, width: '15%' },
    { key: 'value', label: 'Value', sortable: true, width: '15%' },
    { key: 'daysStale', label: 'Age', sortable: true, width: '10%' },
  ],

  analyst: [
    { key: 'clientName', label: 'Case', sortable: true, width: '30%' },
    { key: 'status', label: 'Stage', sortable: true, width: '20%' },
    { key: 'assignedTo', label: 'Assigned', sortable: true, width: '15%' },
    { key: 'daysStale', label: 'Days', sortable: true, width: '15%' },
    { key: 'value', label: 'Value', sortable: true, width: '10%' },
    { key: 'lastUpdated', label: 'Updated', sortable: true, width: '10%' },
  ],

  client: [
    { key: 'clientName', label: 'Case', sortable: true, width: '40%' },
    { key: 'status', label: 'Status', sortable: true, width: '30%' },
    { key: 'lastUpdated', label: 'Last Updated', sortable: true, width: '30%' },
  ],
  mediator: [],  // Phase 7 neutral: no WorkDesk columns (stripped workspace)
  public_adjuster: [],  // PR1: no WorkDesk columns yet
  carrier_adjuster: [],  // PR1: no WorkDesk columns yet
};

export const ROLE_COLUMNS: Record<WorkspaceRole, (typeof ROLE_COLUMNS_BASE)[keyof typeof ROLE_COLUMNS_BASE]> = {
  ...ROLE_COLUMNS_BASE,
  attorney_internal: ROLE_COLUMNS_BASE.attorney,  // mirrors attorney
};

/**
 * Get the "needs-action" metric key for each role
 * This determines which metric card gets the orange left-accent
 */
export const NEEDS_ACTION_KEYS: Record<WorkspaceRole, string> = {
  attorney: 'demands_outstanding',
  accountant_internal: 'pending_review',
  accountant_external: 'available_for_review',
  data_specialist: 'transactions_to_classify',
  admin: 'pending_tasks',
  analyst: 'pending_review',
  client: 'pending_actions',
  mediator: '',  // Phase 7 neutral: no WorkDesk / no needs-action surface
  public_adjuster: '',  // PR1: no WorkDesk yet
  carrier_adjuster: '',  // PR1: no WorkDesk yet
  attorney_internal: 'demands_outstanding',  // mirrors attorney
};

/**
 * Get metrics for a specific role
 */
export function getRoleMetrics(role: WorkspaceRole): MetricConfig[] {
  return ROLE_METRICS[role] || ROLE_METRICS.analyst;
}

/**
 * Get columns for a specific role
 */
export function getRoleColumns(role: WorkspaceRole): Array<{
  key: keyof WorkDeskCase | string;
  label: string;
  sortable?: boolean;
  width?: string;
}> {
  return ROLE_COLUMNS[role] || ROLE_COLUMNS.analyst;
}

/**
 * Get the needs-action key for a specific role
 */
export function getNeedsActionKey(role: WorkspaceRole): string {
  return NEEDS_ACTION_KEYS[role] || 'pending_tasks';
}
